import { createFileRoute } from "@tanstack/react-router";
import { t } from "i18next";
import { Fragment, useMemo } from "react";

import { BasicVideoPlayer } from "~/components/basic-video-player";
import { MirageLogoSmall } from "~/components/mirage-logo";
import { NoiseGrid } from "~/components/noise-grid";
import { LinkButton } from "~/components/primitives/button";
import { PriceBox } from "~/modules/checkout/components/price-box";
import { getCheckoutInfoQuery } from "~/modules/checkout/network/queries";
import { getBusinessPlanPriceInfo, getStripeKeyMode } from "~/modules/checkout/utils";
import { css } from "~/styled-system/css";

const CONTACT_SALES_LINK = "https://captions.typeform.com/to/wDrnBiRN";

export const Route = createFileRoute("/pay/")({
  component: RouteComponent,
});

function RouteComponent() {
  const keyMode = getStripeKeyMode();
  const { data } = getCheckoutInfoQuery.useSuspenseQuery({
    params: { stripeKeyMode: keyMode },
  });

  const priceDisplayInfo = useMemo(() => getBusinessPlanPriceInfo(data), [data]);

  if (!priceDisplayInfo) {
    throw new Error("No price display info");
  }

  const content = (
    <div className={boxStyles}>
      <aside className={css({ padding: "16px" })}>
        <div className={videoStyles}>
          <BasicVideoPlayer
            src="https://stream.mux.com/zYZv6kPGJv01qruRq6JNGVJDlzUcyPgG0000YWKaRPeKcI.m3u8"
            autoPlay
            loop
            muted
            playsInline
            controls={{ progress: true, muted: true }}
            theme={{
              fullWidth: true,
              fullHeight: true,
            }}
          />
        </div>
      </aside>
      <div className={infoStyles}>
        <MirageLogoSmall />
        <h1 className={css({ textStyle: "heading-4", color: "$gray.100" })}>
          {t("mirage-studio:pay.title")}
        </h1>
        <p className={css({ textStyle: "body-1-subtle", color: "$gray.400" })}>
          {t("mirage-studio:pay.item-1")}
        </p>
        <p className={css({ textStyle: "body-1-subtle", color: "$gray.400" })}>
          {t("mirage-studio:pay.item-2")}
        </p>
        <p className={css({ textStyle: "body-1-subtle", color: "$gray.400" })}>
          {t("mirage-studio:teams.paywall.item-3")}
        </p>
        <PriceBox priceDisplayInfo={priceDisplayInfo} className={css({ marginTop: "auto" })} />
        <div className={buttonGroupStyles}>
          <LinkButton
            href={CONTACT_SALES_LINK}
            target="_blank"
            rel="noopener noreferrer"
            size="lg"
            variant="secondary"
          >
            {t("mirage-studio:pay.sales-cta")}
          </LinkButton>
          <LinkButton size="lg" to="/pay/checkout">
            {t("common:buy-now")}
          </LinkButton>
        </div>
      </div>
    </div>
  );

  return (
    <Fragment>
      <NoiseGrid />
      {content}
    </Fragment>
  );
}

const boxStyles = css({
  backgroundColor: "$surface.translucent",
  backdropFilter: "blur(2px)",
  boxShadow: "$highlight",

  display: "flex",
  alignItems: "stretch",
  width: "100%",
  maxWidth: "730px",

  borderRadius: "32px",
  pointerEvents: "none",
  "& :is(button, a, input, textarea)": {
    pointerEvents: "auto",
  },
});

const infoStyles = css({
  flex: 1,

  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "24px",
});

const buttonGroupStyles = css({
  display: "flex",
  gap: "8px",
  width: "100%",
  marginBottom: "0",
  "& > *": {
    flexGrow: 1,
  },
});

const videoStyles = css({
  width: "320px",
  aspectRatio: "9/16",
  borderRadius: "16px",
  flexShrink: 0,
  overflow: "hidden",
  pointerEvents: "auto",
  "@media (max-width: 768px)": {
    display: "none",
  },
});
